/**
 * El contenido en Markdown de un enunciado, una pista o una opción.
 *
 * Existe porque el mismo trabajo estaba repetido a medias en cinco sitios. La vista previa
 * renderizaba **y** componía las fórmulas; el proyector, el móvil del alumno y las analíticas
 * inyectaban el HTML en crudo y no componían nada. Resultado: un `$x^2$` se veía compuesto
 * mientras el profesor lo escribía y se proyectaba en TeX literal en la pizarra, que es
 * exactamente al revés de lo que hace falta.
 *
 * Aquí se hace lo mismo siempre: renderizar, sanear y componer. Los dos módulos pesados
 * —el renderizador y el compositor— viajan aparte y se cargan solo cuando hay algo que pintar.
 */

import { useEffect, useRef, useState } from 'react';

export interface ContenidoMarkdownProps {
  readonly source: string;
  /** Bloque o línea: una opción de respuesta no lleva párrafos. */
  readonly enLinea?: boolean;
  readonly className?: string | undefined;
  readonly 'data-testid'?: string;
}

export function ContenidoMarkdown({
  source,
  enLinea = false,
  className,
  'data-testid': testId,
}: ContenidoMarkdownProps): React.JSX.Element {
  const [html, setHtml] = useState<string | null>(null);
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelado = false;

    void import('./render.js')
      .then(async ({ renderMarkdown, renderInlineMarkdown }) => {
        if (cancelado) return;
        setHtml(enLinea ? renderInlineMarkdown(source) : renderMarkdown(source));

        // Las fórmulas y el resaltado se componen sobre el DOM ya saneado, en un segundo módulo
        // que también viaja aparte. El `queueMicrotask` espera a que React haya pintado.
        const { enhance } = await import('./enhance.js');
        if (cancelado) return;
        queueMicrotask(() => {
          const raiz = contenedor.current;
          if (raiz !== null) void enhance(raiz);
        });
      })
      .catch((error: unknown) => {
        // Si el renderizador no llega, se enseña el texto tal cual: es feo, pero es el contenido.
        console.error('No se ha podido componer el contenido', error);
        if (!cancelado) setHtml(null);
      });

    return () => {
      cancelado = true;
    };
  }, [source, enLinea]);

  if (html === null) {
    // Mientras llega el módulo se enseña el texto sin formato, no un hueco: en el proyector, un
    // enunciado que aparece medio segundo después es un enunciado que nadie estaba leyendo.
    return (
      <div ref={contenedor} className={className} data-testid={testId}>
        {source}
      </div>
    );
  }

  return (
    <div
      ref={contenedor}
      className={className}
      data-testid={testId}
      // El HTML ya ha pasado por el saneador con lista blanca; insertarlo es seguro por eso, y
      // solo por eso.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
